import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, User, ArrowLeft, Share2, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { FoundingPhilosophyBadge } from '@/components/FoundingPhilosophyBadge';

const WhenMoreIsNoLongerEnough = () => {
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate('/blog');
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={handleGoBack}
            className="mb-8 -ml-4 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Button>
          
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Badge>Philosophy</Badge>
              <FoundingPhilosophyBadge />
            </div>
            <h1 className="text-4xl md:text-6xl font-serif font-light mb-6 text-foreground leading-tight">
              When "More" Is No Longer Enough
            </h1>
            <p className="text-xl text-muted-foreground font-light italic mb-4">
              On the quiet shift from achievement to peace
            </p>
            
            <div className="flex items-center gap-6 text-sm text-muted-foreground mb-8">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>June 15, 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>8 min read</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>Piotr</span>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <Button variant="outline" size="sm">
                <Share2 className="w-4 h-4 mr-2" />
                Share Article
              </Button>
            </div>
          </div>
          
          <div className="relative overflow-hidden rounded-2xl mb-12">
            <img 
              src="/lovable-uploads/when-more-is-no-longer-enough.jpg" 
              alt="Person sitting alone on bench overlooking ocean, contemplating life and finding peace in stillness"
              className="w-full h-[400px] md:h-[500px] object-cover grayscale"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="px-6 pb-24">
        <div className="max-w-4xl mx-auto">
          {/* Founding Philosophy Callout */}
          <div className="mb-12 p-6 rounded-2xl bg-gradient-to-r from-primary/5 via-accent/5 to-primary/5 border border-primary/10">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-serif font-medium text-foreground mb-2">Founding Philosophy</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  This article represents one of the core principles that inspired the creation of Mystic Forest — 
                  a space dedicated to helping people transition from endless striving to meaningful presence.
                </p>
              </div>
            </div>
          </div>
          
          <div className="prose prose-lg max-w-none">
            <blockquote className="border-l-4 border-primary pl-6 my-8 italic text-xl text-muted-foreground">
              "It is not the speed that wears us down, but the noise."
              <footer className="text-sm text-muted-foreground mt-2 not-italic">
                — Pico Iyer, <em>The Art of Stillness: Adventures in Going Nowhere</em>, 2014, p. 17
              </footer>
            </blockquote>
            
            <div className="space-y-8 text-lg leading-relaxed text-foreground">
              <p>
                For much of our lives, we move fast. We collect achievements, experiences, titles, and stories — chasing the next version of "better."
                We call it ambition, drive, progress.
              </p>

              <p>
                Until one day, more stops meaning better.
                The push forward becomes a weight. The rush turns into noise.
              </p>

              <p>
                It's not failure.
                It's awakening.
              </p>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The Overstimulated Mind</h2>

              <p>
                We live in an age of excess — of information, opportunity, and stimulation. Our devices light up by the minute, our calendars overflow, our minds rarely rest.
              </p>

              <p>
                Modern psychology calls this the <em>attention economy</em>: a state in which our focus has become a scarce resource.
                According to the <em>World Mental Health Report</em> (World Health Organization, 2022, p. 14), nearly one in four adults globally experiences stress-related mental-health challenges — often amplified by digital overload and performance pressure.
              </p>

              <p>
                We scroll, we multitask, we measure ourselves against invisible benchmarks.
                And slowly, we forget what silence feels like.
              </p>

              <blockquote className="border-l-4 border-primary pl-6 my-8 italic text-xl text-muted-foreground">
                "We have sacrificed conversation for mere connection."
                <footer className="text-sm text-muted-foreground mt-2 not-italic">
                  — Sherry Turkle, <em>Reclaiming Conversation: The Power of Talk in a Digital Age</em>, 2015, p. 5
                </footer>
              </blockquote>

              <p>
                In the constant hum of updates and notifications, connection becomes noise — and solitude, a rare luxury.
              </p>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">When Values Begin to Shift</h2>

              <p>
                At some point, the internal dialogue changes.
                The question is no longer "How can I do more?" but "What actually matters?"
              </p>

              <p>
                This shift often follows periods of deep change — a breakup, burnout, a layoff, or quiet exhaustion. We begin to understand that endless striving leaves little space for joy.
              </p>

              <p>
                Psychologist Abraham Maslow described this transition as a movement from <em>deficiency needs</em> to <em>being needs</em> — the pursuit of meaning, authenticity, and connection.
              </p>

              <blockquote className="border-l-4 border-primary pl-6 my-8 italic text-xl text-muted-foreground">
                "The good society is one in which virtue pays."
                <footer className="text-sm text-muted-foreground mt-2 not-italic">
                  — Abraham H. Maslow, <em>Motivation and Personality</em>, 1954, p. 236
                </footer>
              </blockquote>

              <p>
                True fulfillment, he argued, doesn't come from achievement but from alignment — from living according to one's values and purpose.
              </p>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The Myth of the Midlife Crisis</h2>

              <p>
                We often label this turning point a <em>midlife crisis</em> — a sign of disillusionment or decline.
                But what if it's not a crisis at all?
              </p>

              <blockquote className="border-l-4 border-primary pl-6 my-8 italic text-xl text-muted-foreground">
                "Maturity means freedom from striving for simple approval."
                <footer className="text-sm text-muted-foreground mt-2 not-italic">
                  — Jane Loevinger, <em>Ego Development: Conceptions and Theories</em>, 1976, p. 45
                </footer>
              </blockquote>

              <p>
                Long-term findings from the Harvard Study of Adult Development show that human happiness tends to rise when people shift their priorities from achievement to relationships and emotional connection (Vaillant, <em>Triumphs of Experience</em>, 2012, pp. 278–282).
              </p>

              <p>
                So perhaps it's not collapse, but calibration — a necessary return to balance.
              </p>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The Seed of Mystic Forest</h2>

              <p>
                Mystic Forest was born from this realization — that the pursuit of "more" had taken us far from ourselves.
              </p>

              <p>
                Our retreats and workshops were never meant as escapes.
                They are invitations back to life: to the body, to stillness, to the simple rhythm of nature.
              </p>

              <p>We believe that:</p>
              
              <ul className="space-y-3 ml-6">
                <li>Peace of mind is not the opposite of ambition, but its evolution.</li>
                <li>Simplicity is not emptiness, but clarity.</li>
                <li>Connection is the true measure of abundance.</li>
              </ul>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The Art of Enough</h2>

              <p>
                Maybe the real journey isn't about reaching higher, but arriving deeper.
                Not faster, but gentler.
                Not more, but enough.
              </p>

              <p>
                Because at some point, we stop chasing what impresses others — and start seeking what nourishes us.
              </p>

              <p>
                And that's when life finally starts to feel like our own.
              </p>

              <p className="text-xl font-light italic text-accent">
                In the quiet shift from achievement to peace, we find our true selves.
              </p>

              <div className="bg-card p-8 rounded-2xl mt-16 border border-border/20">
                <h3 className="text-2xl font-serif font-light mb-4 text-foreground">References & Inspirations</h3>
                <ol className="space-y-2 text-sm text-muted-foreground list-decimal list-inside">
                  <li>World Health Organization. <em>World Mental Health Report: Transforming Mental Health for All</em>. Geneva: WHO, 2022, p. 14.</li>
                  <li>Turkle, Sherry. <em>Reclaiming Conversation: The Power of Talk in a Digital Age</em>. New York: Penguin Press, 2015, p. 5.</li>
                  <li>Maslow, Abraham H. <em>Motivation and Personality</em>. New York: Harper & Row, 1954, p. 236.</li>
                  <li>Loevinger, Jane. <em>Ego Development: Conceptions and Theories</em>. San Francisco: Jossey-Bass, 1976, p. 45.</li>
                  <li>Vaillant, George E. <em>Triumphs of Experience: The Men of the Harvard Grant Study</em>. Cambridge, MA: Harvard University Press, 2012, pp. 278–282.</li>
                  <li>Iyer, Pico. <em>The Art of Stillness: Adventures in Going Nowhere</em>. New York: TED Books, 2014, p. 17.</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default WhenMoreIsNoLongerEnough;
